import React, { useState, useEffect } from "react";
import { Copy, ChevronDown, Save, Layout, Maximize, Palette, Sparkles, MessageSquare, Code, Type, Square, Image as ImageIcon, Check, Plus, Activity, Brain, Zap } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";

const WidgetIntegration = ({ activeAgentId, agents, fetchAgents }) => {
  const { showToast } = useToast();
  const [widgetConfig, setWidgetConfig] = useState({
    position: "",
    welcomeMessage: "",
    title: "",
    iconUrl: "",
    brandLogoUrl: "",
    headerColor: "#4c3850",
    userBubbleColor: "#4c3850",
    botBubbleColor: "#ffffff",
    sendButtonColor: "#4c3850",
    size: "medium",
    showCloseButton: true,
    showLauncher: true,
    eyecatcherEnabled: true,
    eyecatcherText: "👋 Ask me anything!",
    eyecatcherTheme: "gradient",
    eyecatcherAnimation: "pulse",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    borderRadius: "20px",
    customParameters: [],
    campaignSequentialDisplay: true,
    learningEnabled: true,
    feedbackEnabled: true,
    summaryEnabled: true,
    summaryInterval: 5,
    summaryModelId: "gpt-4o-mini",
    mode: "multi",
  });
  const currentAgent = agents?.find((p) => p.id === activeAgentId);
  const currentAgentKey = currentAgent?.agent_key || "EC-88888";
  const [uploading, setUploading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const [summaryModelDropdownOpen, setSummaryModelDropdownOpen] = useState(false);
  const [eyecatcherThemeDropdownOpen, setEyecatcherThemeDropdownOpen] = useState(false);
  const [eyecatcherAnimationDropdownOpen, setEyecatcherAnimationDropdownOpen] = useState(false);
  const [iconPreview, setIconPreview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  const SUMMARY_MODEL_OPTIONS = [
    { value: "gpt-4o-mini", label: "GPT-4o Mini (Cost Optimized)" },
    { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo (Standard)" },
    { value: "gpt-4o", label: "GPT-4o (High Precision)" },
    { value: "claude-3-5-haiku-20241022", label: "Claude 3.5 Haiku" },
    { value: "gemini-1.5-flash", label: "Gemini 1.5 Flash" },
  ];

  const RESERVED_KEYS = [
    "apiUrl", "agentId", "agentKey", "showCloseButton", "showLauncher",
    "title", "welcomeMessage", "position", "size", "primaryColor",
    "secondaryColor", "backgroundColor", "textColor", "borderRadius",
    "width", "height", "fontSize", "fontFamily", "isPreview"
  ];

  const positionOptions = [
    { value: "", label: "Select Position" },
    { value: "bottom-right", label: "Bottom Right" },
    { value: "bottom-left", label: "Bottom Left" },
    { value: "top-right", label: "Top Right" },
    { value: "top-left", label: "Top Left" },
  ];

  const sizeOptions = [
    { value: "small", label: "Small (300×400)" },
    { value: "medium", label: "Medium (350×500)" },
    { value: "large", label: "Large (400×600)" },
  ];

  const fontOptions = [
    { label: 'Inter (Default)', value: 'Inter, sans-serif' },
    { label: 'Roboto', value: 'Roboto, sans-serif' },
    { label: 'Open Sans', value: '"Open Sans", sans-serif' },
    { label: 'Montserrat', value: 'Montserrat, sans-serif' },
    { label: 'Poppins', value: 'Poppins, sans-serif' },
    { label: 'Playfair Display', value: '"Playfair Display", serif' },
    { label: 'Lobster', value: 'Lobster, cursive' },
    { label: 'Courier New', value: '"Courier New", monospace' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Comic Sans MS', value: '"Comic Sans MS", cursive' },
    { label: 'System Sans', value: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }
  ];
  const eyecatcherThemeOptions = [
    { value: 'gradient', label: 'Gradient' },
    { value: 'glassmorphic', label: 'Glassmorphic' },
    { value: 'classic', label: 'Classic' },
  ];
  const eyecatcherAnimationOptions = [
    { value: 'pulse', label: 'Pulse' },
    { value: 'shake', label: 'Shake' },
    { value: 'float', label: 'Float' },
  ];
  const [embedCode, setEmbedCode] = useState("");
  const [paramDropdownOpen, setParamDropdownOpen] = useState(false);
  const COMMON_PARAMS = [
    { label: "User Full Name", value: "name" },
    { label: "Email Address", value: "email" },
    { label: "Phone Number", value: "phone" },
    { label: "Custom User ID", value: "userid" },
  ];

  const backendUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
  const frontendUrl = backendUrl;

  useEffect(() => {
    if (!agents || agents.length === 0) {
      fetchAgents();
    }
  }, [agents, fetchAgents]);

  useEffect(() => {
    const loadAllConfig = async () => {
      try {
        const [configResponse, campaignsList] = await Promise.all([
          api.getWidgetConfig(),
          api.getCampaigns().catch(() => [])
        ]);

        if (configResponse) {
          setWidgetConfig({
            position: configResponse.position || "",
            welcomeMessage: configResponse.welcome_message || "",
            title: configResponse.title || "",
            iconUrl: configResponse.icon_url || "",
            brandLogoUrl: configResponse.brand_logo_url || "",
            headerColor: configResponse.header_color || "#4c3850",
            userBubbleColor: configResponse.user_bubble_color || "#4c3850",
            botBubbleColor: configResponse.bot_bubble_color || "#ffffff",
            sendButtonColor: configResponse.send_button_color || "#4c3850",
            size: configResponse.size || "medium",
            showCloseButton: configResponse.show_close_button !== undefined ? configResponse.show_close_button : true,
            showLauncher: configResponse.show_launcher !== undefined ? configResponse.show_launcher : true,
            eyecatcherEnabled: configResponse.eyecatcher_enabled !== undefined ? configResponse.eyecatcher_enabled : true,
            eyecatcherText: configResponse.eyecatcher_text || "👋 Ask me anything!",
            eyecatcherTheme: configResponse.eyecatcher_theme || "gradient",
            eyecatcherAnimation: configResponse.eyecatcher_animation || "pulse",
            fontFamily: configResponse.font_family || "Inter, sans-serif",
            fontSize: configResponse.font_size || "14px",
            borderRadius: configResponse.border_radius || "20px",
            campaignSequentialDisplay: configResponse.campaign_sequential_display !== undefined ? configResponse.campaign_sequential_display : true,
            learningEnabled: configResponse.learning_enabled !== undefined ? configResponse.learning_enabled : true,
            feedbackEnabled: configResponse.feedback_enabled !== undefined ? configResponse.feedback_enabled : true,
            summaryEnabled: configResponse.summary_enabled !== undefined ? configResponse.summary_enabled : true,
            summaryInterval: configResponse.summary_interval || 5,
            summaryModelId: configResponse.summary_model_id || "gpt-4o-mini",
            customParameters: configResponse.custom_parameters || [],
            mode: configResponse.mode || "multi",
          });

          if (configResponse.icon_url) {
            const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
            setIconPreview(`${baseUrl}${configResponse.icon_url}`);
          }
        }

        if (campaignsList) setCampaigns(campaignsList);
      } catch (error) {
        console.error("Error loading widget data:", error);
      }
    };

    if (activeAgentId) {
      loadAllConfig();
    }
  }, [activeAgentId]);


  useEffect(() => {
    // Sync current (possibly unsaved) config to sessionStorage for the preview tab
    sessionStorage.setItem('widget_preview_config', JSON.stringify(widgetConfig));
  }, [widgetConfig]);

  useEffect(() => {
    const generateEmbedCode = () => {
      const userStr = sessionStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      const tenantId = currentAgent?.tenant_id || user?.tenant_id || "";

      const code = `<script src="${frontendUrl}/widget.js"></script>
<script>
  ChatWidget.init({
    apiUrl: '${backendUrl}',
    agentId: '${activeAgentId || ""}',
    tenantId: '${tenantId}',
    agentKey: '${currentAgentKey}',
    showCloseButton: ${widgetConfig.showCloseButton},
    showLauncher: ${widgetConfig.showLauncher}${widgetConfig.customParameters.length > 0 ? ',\n    // Custom Parameters:\n    ' + widgetConfig.customParameters.map(p => `${p}: 'VALUE'`).join(',\n    ') : ''}
  });
</script>`;
      setEmbedCode(code);
    };

    if (widgetConfig.title) {
      generateEmbedCode();
    }
  }, [widgetConfig.title, currentAgentKey, activeAgentId, currentAgent, frontendUrl, backendUrl, widgetConfig.showCloseButton, widgetConfig.showLauncher, widgetConfig.customParameters]);

  const saveConfig = async (overrideConfig = null, successMessage = "Configuration saved successfully!") => {
    try {
      if (overrideConfig) {
        // Use PATCH for specific field updates
        await api.patchWidgetConfig(overrideConfig);
      } else {
        // Use PATCH for the main form save to avoid overwriting assets
        const partialConfig = {
          position: widgetConfig.position,
          welcome_message: widgetConfig.welcomeMessage,
          title: widgetConfig.title,
          header_color: widgetConfig.headerColor,
          user_bubble_color: widgetConfig.userBubbleColor,
          bot_bubble_color: widgetConfig.botBubbleColor,
          send_button_color: widgetConfig.sendButtonColor,
          size: widgetConfig.size,
          font_family: widgetConfig.fontFamily,
          font_size: widgetConfig.fontSize,
          border_radius: widgetConfig.borderRadius,
          campaign_sequential_display: widgetConfig.campaignSequentialDisplay,
          learning_enabled: widgetConfig.learningEnabled,
          feedback_enabled: widgetConfig.feedbackEnabled,
          summary_enabled: widgetConfig.summaryEnabled,
          summary_interval: widgetConfig.summaryInterval,
          summary_model_id: widgetConfig.summaryModelId,
          eyecatcher_enabled: widgetConfig.eyecatcherEnabled,
          eyecatcher_text: widgetConfig.eyecatcherText,
          eyecatcher_theme: widgetConfig.eyecatcherTheme,
          eyecatcher_animation: widgetConfig.eyecatcherAnimation,
          mode: widgetConfig.mode,
        };
        await api.patchWidgetConfig(partialConfig);
      }
      showToast(successMessage, "success");
    } catch (error) {
      console.error("Error saving config:", error);
      showToast("Failed to save configuration", "error");
    }
  };

  const handleIconUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const user = sessionStorage.getItem("user");
      const token = user ? JSON.parse(user).token : null;

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/widget/icon/upload`,
        {
          method: "POST",
          headers: {
            "X-Agent-ID": activeAgentId.toString(),
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedConfig = await api.getWidgetConfig();
      if (updatedConfig) {
        setWidgetConfig(prev => ({
          ...prev,
          iconUrl: updatedConfig.icon_url,
          brandLogoUrl: updatedConfig.brand_logo_url,
        }));

        if (updatedConfig.icon_url) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
          setIconPreview(`${baseUrl}${updatedConfig.icon_url}`);
        }
      }

      showToast("Icon uploaded successfully!", "success");
    } catch (error) {
      console.error("Error uploading icon:", error);
      showToast("Failed to upload icon", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleIconUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      showToast("Code copied to clipboard!", "success");
    } catch (error) {
      console.error("Error copying embed code:", error);
      showToast("Failed to copy code", "error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start">
      <div className="w-full max-w-6xl space-y-12">
        <PageHeader 
          title="Widget Interface"
          description="Engineered for seamless integration. Orchestrate your brand's digital presence with a bespoke neural touchpoint."
        />

        <div className="flex flex-col items-center gap-8 animate-slideIn max-w-4xl mx-auto w-full">
          {/* Configuration Section */}
          <div className="flex flex-col gap-8 w-full">
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-xl shadow-gray-100/50">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <Palette size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Visual Identity</h3>
                  <p className="text-[11px] font-medium text-gray-400">Configure layout and color palette</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8 mt-10">
                {/* Row 1 */}
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Layout size={12} /> Position
                  </label>
                  <div className="relative">
                    <div
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="w-full px-5 py-2.5 bg-gray-50 border-0 rounded-xl text-xs text-gray-900 cursor-pointer flex justify-between items-center hover:bg-gray-100 transition-all font-bold uppercase tracking-wider"
                    >
                      <span>
                        {positionOptions.find((opt) => opt.value === widgetConfig.position)
                          ?.label || "Select Position"}
                      </span>
                      <ChevronDown
                        size={16}
                        className={`text-gray-400 transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""}`}
                      />
                    </div>

                    {dropdownOpen && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-2xl mt-2 z-50 shadow-2xl overflow-hidden animate-slideUp">
                        {positionOptions.map((option) => (
                          <div
                            key={option.value}
                            onClick={() => {
                              setWidgetConfig({
                                ...widgetConfig,
                                position: option.value,
                              });
                              setDropdownOpen(false);
                            }}
                            className={`px-5 py-4 text-sm font-medium text-gray-700 cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition-all ${widgetConfig.position === option.value ? "bg-blue-50 text-blue-600" : ""}`}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <Maximize size={12} /> Initial Size
                  </label>
                  <div className="relative">
                    <div
                      onClick={() => setSizeDropdownOpen(!sizeDropdownOpen)}
                      className="w-full px-5 py-2.5 bg-gray-50 border-0 rounded-xl text-xs text-gray-900 cursor-pointer flex justify-between items-center hover:bg-gray-100 transition-all font-bold uppercase tracking-wider"
                    >
                      <span>
                        {sizeOptions.find((opt) => opt.value === widgetConfig.size)
                          ?.label || "Medium (350×500)"}
                      </span>
                      <ChevronDown
                        size={16}
                        className={`text-gray-400 transition-transform duration-300 ${sizeDropdownOpen ? "rotate-180" : ""}`}
                      />
                    </div>

                    {sizeDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-xl mt-2 z-50 shadow-2xl overflow-hidden animate-slideUp">
                        {sizeOptions.map((option) => (
                          <div
                            key={option.value}
                            onClick={() => {
                              setWidgetConfig({
                                ...widgetConfig,
                                size: option.value,
                              });
                              setSizeDropdownOpen(false);
                            }}
                            className={`px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-700 cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition-all ${widgetConfig.size === option.value ? "bg-blue-50 text-blue-600" : ""}`}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 2 */}
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <MessageSquare size={12} /> Widget Display Title
                  </label>
                  <input
                    type="text"
                    value={widgetConfig.title}
                    onChange={(e) =>
                      setWidgetConfig({ ...widgetConfig, title: e.target.value })
                    }
                    placeholder="E.g., Customer Support"
                    className="w-full px-5 py-2.5 bg-gray-50 border-0 rounded-xl text-xs text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 font-bold tracking-tight transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Sparkles size={12} /> Passive Welcome Hook
                  </label>
                  <input
                    type="text"
                    value={widgetConfig.welcomeMessage}
                    onChange={(e) =>
                      setWidgetConfig({
                        ...widgetConfig,
                        welcomeMessage: e.target.value,
                      })
                    }
                    placeholder="How can we help you today?"
                    className="w-full px-5 py-2.5 bg-gray-50 border-0 rounded-xl text-xs text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 font-bold tracking-tight transition-all"
                  />
                </div>

                {/* Row 3 */}
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Type size={12} /> Font Family
                  </label>
                  <div className="relative">
                    <div
                      onClick={() => setFontDropdownOpen(!fontDropdownOpen)}
                      className="w-full px-5 py-2.5 bg-gray-50 border-0 rounded-xl text-xs text-gray-900 cursor-pointer flex justify-between items-center hover:bg-gray-100 transition-all font-bold tracking-tight"
                    >
                      <span>
                        {fontOptions.find((opt) => opt.value === widgetConfig.fontFamily)
                          ?.label || "Select Font"}
                      </span>
                      <ChevronDown
                        size={14}
                        className={`text-gray-400 transition-transform duration-300 ${fontDropdownOpen ? "rotate-180" : ""}`}
                      />
                    </div>

                    {fontDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-xl mt-2 z-50 shadow-2xl overflow-hidden animate-slideUp max-h-64 overflow-y-auto">
                        {fontOptions.map((option) => {
                          const isSelected = widgetConfig.fontFamily === option.value;
                          const primaryFont = option.value.split(',')[0].replace(/['"]/g, '').trim();
                          return (
                            <div
                              key={option.value}
                              onClick={() => {
                                setWidgetConfig({
                                  ...widgetConfig,
                                  fontFamily: option.value,
                                });
                                setFontDropdownOpen(false);
                              }}
                              className={`flex items-center gap-3 px-5 py-3 text-sm text-gray-700 cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition-all ${isSelected ? "bg-blue-50 text-blue-600" : ""}`}
                              style={{ fontFamily: `"${primaryFont}", sans-serif` }}
                            >
                              <span className={`w-4 flex-shrink-0 ${isSelected ? "text-blue-600" : "text-transparent"}`}>
                                <Check size={14} />
                              </span>
                              <span>{option.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Type size={12} /> Font Size
                  </label>
                  <input
                    type="text"
                    value={widgetConfig.fontSize}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, fontSize: e.target.value })}
                    className="w-full px-5 py-2.5 bg-gray-50 border-0 rounded-xl text-xs text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 font-bold tracking-tight transition-all"
                    placeholder="14px"
                  />
                </div>

                {/* Row 4 */}
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Square size={12} /> Border Radius
                  </label>
                  <input
                    type="text"
                    value={widgetConfig.borderRadius}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, borderRadius: e.target.value })}
                    className="w-full px-5 py-2.5 bg-gray-50 border-0 rounded-xl text-xs text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 font-bold tracking-tight transition-all"
                    placeholder="20px"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
                {[
                  { label: "Header", key: "headerColor" },
                  { label: "User", key: "userBubbleColor" },
                  { label: "Bot", key: "botBubbleColor" },
                  { label: "Action", key: "sendButtonColor" }
                ].map((item) => (
                  <div key={item.key} className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{item.label}</label>
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl border border-gray-100">
                      <input
                        type="color"
                        value={widgetConfig[item.key]}
                        onChange={(e) =>
                          setWidgetConfig({
                            ...widgetConfig,
                            [item.key]: e.target.value,
                          })
                        }
                        className="w-8 h-8 rounded-lg cursor-pointer border-0 bg-transparent overflow-hidden"
                      />
                      <span className="text-[10px] font-mono text-gray-500 font-bold uppercase">{widgetConfig[item.key]}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 p-6 bg-gray-50 rounded-3xl border border-gray-100 space-y-8">
                <div className="flex flex-col md:flex-row gap-8 justify-between">
                  <div className="flex items-center justify-between gap-10 flex-1">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-1">Display Header Close Button</h4>
                      <p className="text-[10px] text-gray-400 font-medium tracking-tight">Toggle the visibility of the "X" button in chat window</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={widgetConfig.showCloseButton}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setWidgetConfig({ ...widgetConfig, showCloseButton: newValue });
                        saveConfig({ show_close_button: newValue });
                      }}
                    />
                    <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#83bbe1] group-hover:after:scale-95 transition-all"></div>
                  </label>
                </div>

                <div className="w-px bg-gray-200 hidden md:block"></div>

                <div className="flex items-center justify-between gap-10 flex-1">
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">Show Floating Launcher</h4>
                    <p className="text-[10px] text-gray-400 font-medium  tracking-tight">Hide icon to auto-open chat directly on page load</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={widgetConfig.showLauncher}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        setWidgetConfig({ ...widgetConfig, showLauncher: newValue });
                        try {
                          await api.patchWidgetConfig({ show_launcher: newValue });
                          showToast("Launcher visibility updated!", "success");
                        } catch (error) {
                          console.error("Error updating launcher:", error);
                          showToast("Failed to update launcher", "error");
                        }
                      }}
                    />
                    <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#83bbe1] group-hover:after:scale-95 transition-all"></div>
                  </label>
                </div>
              </div>

              <div className="p-4 bg-white rounded-3xl border border-gray-100 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <h4 className="text-sm font-bold text-gray-900">Eye Catcher Bubble</h4>
                        <p className="text-[11px] font-medium text-gray-400">Show a floating prompt to invite users into the chat experience.</p>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !widgetConfig.eyecatcherEnabled;
                          setWidgetConfig({ ...widgetConfig, eyecatcherEnabled: newValue });
                          try {
                            await api.patchWidgetConfig({ eyecatcher_enabled: newValue });
                            showToast(newValue ? "Eye Catcher enabled" : "Eye Catcher disabled", "success");
                          } catch (error) {
                            console.error("Error updating Eye Catcher setting:", error);
                            showToast("Failed to update Eye Catcher", "error");
                          }
                        }}
                        className={`w-12 h-6 rounded-full transition-colors relative ${widgetConfig.eyecatcherEnabled ? 'bg-[#83bbe1]' : 'bg-gray-200'}`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${widgetConfig.eyecatcherEnabled ? 'left-7' : 'left-1'}`} />
                      </button>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Eye Catcher Text</label>
                      <input
                        value={widgetConfig.eyecatcherText}
                        onChange={(e) => setWidgetConfig({ ...widgetConfig, eyecatcherText: e.target.value })}
                        onBlur={async (e) => {
                          try {
                            await api.patchWidgetConfig({ eyecatcher_text: e.target.value });
                            showToast("Eye Catcher text saved!", "success");
                          } catch (error) {
                            console.error("Error saving Eye Catcher text:", error);
                            showToast("Failed to save Eye Catcher text", "error");
                          }
                        }}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#83bbe1]"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 relative">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Theme</label>
                        <div
                          onClick={() => setEyecatcherThemeDropdownOpen(!eyecatcherThemeDropdownOpen)}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm text-gray-900 cursor-pointer flex items-center justify-between"
                        >
                          <span>{eyecatcherThemeOptions.find((opt) => opt.value === widgetConfig.eyecatcherTheme)?.label || 'Gradient'}</span>
                          <ChevronDown size={14} className={`text-gray-400 transition-transform ${eyecatcherThemeDropdownOpen ? 'rotate-180' : ''}`} />
                        </div>
                        {eyecatcherThemeDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl z-50 shadow-2xl overflow-hidden">
                            {eyecatcherThemeOptions.map((opt) => (
                              <div
                                key={opt.value}
                                onClick={async () => {
                                  setWidgetConfig({ ...widgetConfig, eyecatcherTheme: opt.value });
                                  setEyecatcherThemeDropdownOpen(false);
                                  try {
                                    await api.patchWidgetConfig({ eyecatcher_theme: opt.value });
                                    showToast('Eye Catcher theme updated!', 'success');
                                  } catch (error) {
                                    console.error('Error updating Eye Catcher theme:', error);
                                    showToast('Failed to update theme', 'error');
                                  }
                                }}
                                className={`px-4 py-3 text-sm text-gray-700 cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition-all ${widgetConfig.eyecatcherTheme === opt.value ? 'bg-blue-50 text-blue-600' : ''}`}
                              >
                                {opt.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="space-y-2 relative">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Animation</label>
                        <div
                          onClick={() => setEyecatcherAnimationDropdownOpen(!eyecatcherAnimationDropdownOpen)}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm text-gray-900 cursor-pointer flex items-center justify-between"
                        >
                          <span>{eyecatcherAnimationOptions.find((opt) => opt.value === widgetConfig.eyecatcherAnimation)?.label || 'Pulse'}</span>
                          <ChevronDown size={14} className={`text-gray-400 transition-transform ${eyecatcherAnimationDropdownOpen ? 'rotate-180' : ''}`} />
                        </div>
                        {eyecatcherAnimationDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl z-50 shadow-2xl overflow-hidden">
                            {eyecatcherAnimationOptions.map((opt) => (
                              <div
                                key={opt.value}
                                onClick={async () => {
                                  setWidgetConfig({ ...widgetConfig, eyecatcherAnimation: opt.value });
                                  setEyecatcherAnimationDropdownOpen(false);
                                  try {
                                    await api.patchWidgetConfig({ eyecatcher_animation: opt.value });
                                    showToast('Eye Catcher animation updated!', 'success');
                                  } catch (error) {
                                    console.error('Error updating Eye Catcher animation:', error);
                                    showToast('Failed to update animation', 'error');
                                  }
                                }}
                                className={`px-4 py-3 text-sm text-gray-700 cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition-all ${widgetConfig.eyecatcherAnimation === opt.value ? 'bg-blue-50 text-blue-600' : ''}`}
                              >
                                {opt.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <p className="text-[10px] text-gray-400">The Eye Catcher floats near the launcher and can be dismissed for the current session.</p>
                  </div>
                </div>

              {/* Advanced Engagement Section */}
              <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-xl shadow-gray-100/50 mt-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-[#83bbe1]">
                    <Sparkles size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Advanced Engagement</h3>
                    <p className="text-[11px] font-medium text-gray-400">Orchestrate how campaigns interact with users</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-4 bg-gray-50 rounded-2xl space-y-3">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-1">AI Execution Mode</h4>
                      <p className="text-[11px] text-gray-400 font-medium tracking-tight">Choose how user queries are processed and executed</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div
                        onClick={async () => {
                          setWidgetConfig({ ...widgetConfig, mode: "single" });
                          try {
                            await api.patchWidgetConfig({ mode: "single" });
                            showToast("Solo Agent Mode Enabled", "success");
                          } catch (error) {
                            console.error("Error setting agent mode:", error);
                            showToast("Failed to update agent mode", "error");
                          }
                        }}
                        className={`p-4 rounded-xl cursor-pointer border transition-all ${
                          widgetConfig.mode === "single"
                            ? "bg-white border-[#83bbe1] shadow-md shadow-blue-50"
                            : "bg-white border-gray-100 opacity-60 hover:opacity-100"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Zap size={16} className={widgetConfig.mode === "single" ? "text-blue-500" : "text-gray-400"} />
                          <span className="text-xs font-bold text-gray-900">Solo Agent</span>
                        </div>
                        <p className="text-[10px] text-gray-400 font-medium">One AI agent handles the entire conversation for maximum speed.</p>
                      </div>

                      <div
                        onClick={async () => {
                          setWidgetConfig({ ...widgetConfig, mode: "multi" });
                          try {
                            await api.patchWidgetConfig({ mode: "multi" });
                            showToast("Agent Team Mode Enabled", "success");
                          } catch (error) {
                            console.error("Error setting agent mode:", error);
                            showToast("Failed to update agent mode", "error");
                          }
                        }}
                        className={`p-4 rounded-xl cursor-pointer border transition-all ${
                          widgetConfig.mode === "multi"
                            ? "bg-white border-[#83bbe1] shadow-md shadow-blue-50"
                            : "bg-white border-gray-100 opacity-60 hover:opacity-100"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Brain size={16} className={widgetConfig.mode === "multi" ? "text-[#83bbe1]" : "text-gray-400"} />
                          <span className="text-xs font-bold text-gray-900">Agent Team</span>
                        </div>
                        <p className="text-[10px] text-gray-400 font-medium">A coordinated team of AI agents collaborates to provide deeper analysis and better decision-making.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                    <div className="space-y-1">
                      <h4 className="text-sm font-bold text-gray-900">Sequential Campaign Queue</h4>
                      <p className="text-[11px] font-medium text-gray-400">If multiple campaigns match, show them one after another instead of just the first one.</p>
                    </div>
                    <button
                      onClick={async () => {
                        const newValue = !widgetConfig.campaignSequentialDisplay;
                        setWidgetConfig({ ...widgetConfig, campaignSequentialDisplay: newValue });
                        try {
                          await api.patchWidgetConfig({ campaign_sequential_display: newValue });
                          showToast("Sequential queue updated!", "success");
                        } catch (error) {
                          console.error("Error updating sequential queue:", error);
                          showToast("Failed to update queue setting", "error");
                        }
                      }}
                      className={`w-12 h-6 rounded-full transition-colors relative ${widgetConfig.campaignSequentialDisplay ? 'bg-[#83bbe1]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${widgetConfig.campaignSequentialDisplay ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold text-gray-900">AI Learning & Memory</h4>
                      </div>
                      <p className="text-[11px] font-medium text-gray-400">Enable background memory extraction.</p>
                    </div>
                    <button
                      onClick={async () => {
                        const newValue = !widgetConfig.learningEnabled;
                        setWidgetConfig({ ...widgetConfig, learningEnabled: newValue });
                        try {
                          await api.patchWidgetConfig({ learning_enabled: newValue });
                          showToast(newValue ? "AI Learning Enabled" : "AI Learning Disabled", "success");
                        } catch (error) {
                          console.error("Error updating learning setting:", error);
                          showToast("Failed to update learning setting", "error");
                        }
                      }}
                      className={`w-12 h-6 rounded-full transition-colors relative ${widgetConfig.learningEnabled ? 'bg-[#83bbe1]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${widgetConfig.learningEnabled ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold text-gray-900">Feedback System</h4>
                      </div>
                      <p className="text-[11px] font-medium text-gray-400">Allow users to rate and comment on neural responses.</p>
                    </div>
                    <button
                      onClick={async () => {
                        const newValue = !widgetConfig.feedbackEnabled;
                        setWidgetConfig({ ...widgetConfig, feedbackEnabled: newValue });
                        try {
                          await api.patchWidgetConfig({ feedback_enabled: newValue });
                          showToast(newValue ? "Feedback Enabled" : "Feedback Disabled", "success");
                        } catch (error) {
                          console.error("Error updating feedback setting:", error);
                          showToast("Failed to update feedback setting", "error");
                        }
                      }}
                      className={`w-12 h-6 rounded-full transition-colors relative ${widgetConfig.feedbackEnabled ? 'bg-[#83bbe1]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${widgetConfig.feedbackEnabled ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-2xl space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-gray-900">Neural Memory Summarization</h4>
                        </div>
                        <p className="text-[11px] font-medium text-gray-400">Distill long conversations into summaries to optimize memory.</p>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !widgetConfig.summaryEnabled;
                          setWidgetConfig({ ...widgetConfig, summaryEnabled: newValue });
                          try {
                            await api.patchWidgetConfig({ summary_enabled: newValue });
                            showToast(newValue ? "Summarization Enabled" : "Summarization Disabled", "success");
                          } catch (error) {
                            console.error("Error updating summary setting:", error);
                            showToast("Failed to update summary setting", "error");
                          }
                        }}
                        className={`w-12 h-6 rounded-full transition-colors relative ${widgetConfig.summaryEnabled ? 'bg-[#83bbe1]' : 'bg-gray-200'}`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${widgetConfig.summaryEnabled ? 'left-7' : 'left-1'}`} />
                      </button>
                    </div>

                    {widgetConfig.summaryEnabled && (
                      <div className="pt-2 space-y-4">
                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Summary Intelligence Model</label>
                          <div className="relative">
                            <div 
                              onClick={() => setSummaryModelDropdownOpen(!summaryModelDropdownOpen)}
                              className="w-full px-4 py-2 bg-white border border-gray-100 rounded-xl text-[10px] font-bold text-gray-700 cursor-pointer flex justify-between items-center hover:bg-gray-50 transition-all uppercase tracking-wider"
                            >
                              <span>{SUMMARY_MODEL_OPTIONS.find(o => o.value === widgetConfig.summaryModelId)?.label || "GPT-4o Mini (Default)"}</span>
                              <ChevronDown size={14} className={`text-gray-400 transition-transform ${summaryModelDropdownOpen ? 'rotate-180' : ''}`} />
                            </div>
                            {summaryModelDropdownOpen && (
                              <div className="absolute bottom-full mb-2 left-0 right-0 bg-white border border-gray-100 rounded-xl z-50 shadow-2xl overflow-hidden animate-slideUp">
                                {SUMMARY_MODEL_OPTIONS.map(opt => (
                                  <div 
                                    key={opt.value}
                                    onClick={() => {
                                      const newConfig = { ...widgetConfig, summaryModelId: opt.value };
                                      setWidgetConfig(newConfig);
                                      saveConfig({ summary_model_id: opt.value }, `Summarization model set to ${opt.label}`);
                                      setSummaryModelDropdownOpen(false);
                                    }}
                                    className={`px-4 py-3 text-[10px] font-bold uppercase tracking-wider cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition-all ${widgetConfig.summaryModelId === opt.value ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}`}
                                  >
                                    {opt.label}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium text-gray-600">Summarization Interval</span>
                            <span className="text-xs font-bold text-[#83bbe1]">Every {widgetConfig.summaryInterval} messages</span>
                          </div>
                          <input
                            type="range"
                            min="1"
                            max="20"
                            step="1"
                            value={widgetConfig.summaryInterval}
                            onChange={(e) => setWidgetConfig({ ...widgetConfig, summaryInterval: parseInt(e.target.value) })}
                            onMouseUp={async () => {
                              try {
                                await api.patchWidgetConfig({ summary_interval: widgetConfig.summaryInterval });
                                showToast(`Interval set to ${widgetConfig.summaryInterval} messages`, "success");
                              } catch (error) {
                                console.error("Error updating summary interval:", error);
                                showToast("Failed to update interval", "error");
                              }
                            }}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#83bbe1]"
                          />
                          <div className="flex justify-between text-[10px] text-gray-400 font-medium">
                            <span>1 (Aggressive)</span>
                            <span>20 (Lazy)</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Neural Context Tokens Section */}
              <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl shadow-gray-100/50 mt-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-[#83bbe1]">
                    <Sparkles size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Neural Context Parameters</h3>
                    <p className="text-[11px] font-medium text-gray-400">Register custom parameters to pass from your site directly to the agent's memory.</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Common Parameters Dropdown */}
                    <div className="relative flex-1">
                      <div
                        onClick={() => setParamDropdownOpen(!paramDropdownOpen)}
                        className="w-full px-5 py-2.5 bg-[#f0f7ff] border border-[#83bbe1]/30 rounded-xl text-xs text-[#83bbe1] cursor-pointer flex justify-between items-center hover:bg-[#83bbe1]/10 transition-all font-bold tracking-tight"
                      >
                        <span className="flex items-center gap-2">
                          <Plus size={14} /> Inbuilt Parameters
                        </span>
                        <ChevronDown size={14} className={`transition-transform duration-300 ${paramDropdownOpen ? 'rotate-180' : ''}`} />
                      </div>
                      {paramDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 bg-white border border-[#83bbe1]/20 rounded-xl mt-2 z-50 shadow-2xl overflow-hidden animate-slideUp">
                          {COMMON_PARAMS.map((opt) => {
                            const isRegistered = widgetConfig.customParameters.includes(opt.value);
                            return (
                              <div
                                key={opt.value}
                                onClick={() => {
                                  if (isRegistered) {
                                    showToast(`"${opt.value}" is already registered`, "warning");
                                  } else {
                                    const newParams = [...widgetConfig.customParameters, opt.value];
                                    setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                                    saveConfig({ custom_parameters: newParams }, `${opt.label} added!`);
                                  }
                                  setParamDropdownOpen(false);
                                }}
                                className={`px-5 py-3 text-xs font-bold uppercase tracking-wider flex justify-between items-center cursor-pointer transition-all ${
                                  isRegistered ? "bg-gray-50 text-gray-300 cursor-not-allowed" : "text-gray-700 hover:bg-[#f0f7ff] hover:text-[#83bbe1]"
                                }`}
                              >
                                <span>{opt.label}</span>
                                <span className="text-[10px] font-mono opacity-50">{opt.value}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center text-gray-300 px-2 font-bold text-[10px] uppercase">Or</div>

                    {/* Manual Input */}
                    <div className="flex gap-2 flex-[1.5]">
                      <input
                        id="new-param-input"
                        type="text"
                        placeholder="e.g. order_id"
                        className="flex-1 px-5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs text-gray-900 focus:ring-2 focus:ring-[#83bbe1] focus:border-[#83bbe1] outline-none placeholder:text-gray-400 font-bold tracking-tight transition-all"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const val = e.target.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
                            if (!val) return;
                            if (RESERVED_KEYS.some(k => k.toLowerCase() === val)) {
                              showToast(`"${val}" is reserved.`, "error");
                              return;
                            }
                            if (!widgetConfig.customParameters.includes(val)) {
                              const newParams = [...widgetConfig.customParameters, val];
                              setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                              saveConfig({ custom_parameters: newParams }, "Custom parameter added!");
                              e.target.value = '';
                            } else {
                              showToast(`"${val}" exists.`, "warning");
                            }
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          const input = document.getElementById('new-param-input');
                          const val = input.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
                          if (!val) return;
                          if (RESERVED_KEYS.some(k => k.toLowerCase() === val)) {
                            showToast(`"${val}" is reserved.`, "error");
                            return;
                          }
                          if (!widgetConfig.customParameters.includes(val)) {
                            const newParams = [...widgetConfig.customParameters, val];
                            setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                            saveConfig({ custom_parameters: newParams }, "Custom parameter added!");
                            input.value = '';
                          }
                        }}
                        className="px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-bold text-[10px] uppercase tracking-widest active:scale-95 shadow-md shadow-gray-200"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {widgetConfig.customParameters.length === 0 ? (
                      <div className="w-full py-8 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-relaxed">
                          No custom parameters registered yet.<br />Parameters allow your app to talk to the AI without user input.
                        </p>
                      </div>
                    ) : (
                      widgetConfig.customParameters.map((param) => (
                        <div
                          key={param}
                          className="group flex items-center gap-3 px-4 py-2 bg-white border border-[#83bbe1]/20 rounded-xl shadow-sm hover:shadow-md hover:border-[#83bbe1]/50 transition-all cursor-default"
                        >
                          <span className="text-[11px] font-black text-[#111826] font-mono">
                            {param}
                          </span>
                          <button
                            onClick={() => {
                              const newParams = widgetConfig.customParameters.filter(p => p !== param);
                              setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                              saveConfig({ custom_parameters: newParams }, "Neural parameter removed!");
                            }}
                            className="text-gray-300 hover:text-red-500 transition-colors"
                          >
                            <ChevronDown size={14} className="rotate-45" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  <p className="text-[9px] text-gray-400 font-medium uppercase tracking-tighter leading-relaxed">
                    * Note: Only lowercase alphanumeric and underscores are allowed. Once registered, these parameters will appear in your deployment script for your developers to populate.
                  </p>

                  <div className="flex justify-center mt-8">
                    <button
                      onClick={() => saveConfig()}
                      className="flex items-center justify-center gap-3 px-10 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-bold text-xs uppercase tracking-widest shadow-lg shadow-gray-200 active:scale-95"
                    >
                      <Save size={14} />
                      Persist UI Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* New Sections: Live Campaigns */}
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl shadow-gray-100/50">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-600">
                    <Activity size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Live Engagement Protocols</h3>
                    <p className="text-[11px] font-medium text-gray-400">Currently active campaigns on your widget</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  {campaigns.filter(c => c.is_active).length} Active
                </div>
              </div>

              <div className="space-y-3">
                {campaigns.filter(c => c.is_active).length === 0 ? (
                  <div className="py-6 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">No active protocols detected</p>
                  </div>
                ) : (
                  campaigns.filter(c => c.is_active).map(campaign => (
                    <div key={campaign.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 hover:border-green-200 transition-all group">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-sm border border-gray-100 group-hover:scale-110 transition-transform">
                          {campaign.data?.image ? (
                            campaign.data.image.startsWith('data:') || campaign.data.image.startsWith('http') ? (
                              <img
                                src={campaign.data.image}
                                alt={campaign.data?.name || 'Campaign'}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-lg">{campaign.data.image}</span>
                            )
                          ) : (
                            '🏷️'
                          )}
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-900">{campaign.data?.name || 'Untitled Campaign'}</h4>
                          <p className="text-[10px] text-gray-500 font-medium">Priority: P{campaign.priority} • Type: {campaign.data?.type || 'Standard'}</p>
                        </div>
                      </div>
                      <div className="px-3 py-1 bg-white border border-gray-100 rounded-lg text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                        Live Now
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>


            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                    <Code size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Deployment Script</h3>
                    <p className="text-[11px] font-medium text-gray-400">Inject into your site header</p>
                  </div>
                </div>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 text-gray-600 rounded-xl hover:bg-gray-100 transition-all font-bold text-xs"
                >
                  <Copy size={14} />
                  Copy Script
                </button>
              </div>

              <div className="relative group">
                <pre className="bg-gray-900 p-8 rounded-xl text-xs border-0 font-mono text-blue-400 leading-loose shadow-inner h-fit w-full whitespace-pre-wrap break-all">
                  <code>{embedCode || "// Generating unique script..."}</code>
                </pre>
              </div>
            </div>
          </div>

          {/* Deploy Logic & Avatar Section */}
          <div className="flex flex-col gap-8 w-full">
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-xl shadow-gray-100/50">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <ImageIcon size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Identity Avatar</h3>
                  <p className="text-[11px] font-medium text-gray-400">Visible brand trigger</p>
                </div>
              </div>

              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() =>
                  !uploading && document.getElementById("icon-input").click()
                }
                className={`group relative border-2 border-dashed border-gray-100 rounded-[24px] p-8 text-center bg-gray-50/50 hover:bg-white hover:border-blue-200 transition-all duration-300 ${uploading ? "cursor-not-allowed" : "cursor-pointer"}`}
              >
                {uploading ? (
                  <div className="flex flex-col items-center py-6">
                    <div className="w-10 h-10 border-4 border-gray-100 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    {iconPreview ? (
                      <div className="mb-6">
                        <img
                          src={iconPreview}
                          alt="Widget Icon"
                          className="w-24 h-24 object-contain rounded-2xl shadow-xl ring-4 ring-white ring-offset-4 ring-gray-50/50"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center text-gray-200 mb-6 shadow-sm border border-gray-50 group-hover:scale-105 transition-transform duration-300">
                        <MessageSquare size={40} />
                      </div>
                    )}
                    <h4 className="text-sm font-bold text-gray-900 mb-2">Change Avatar</h4>
                    <p className="text-[10px] text-gray-400 font-medium leading-relaxed uppercase tracking-tighter">
                      Drag and drop here or<br />click to select file
                    </p>
                  </div>
                )}
              </div>

              <input
                id="icon-input"
                type="file"
                accept="image/*"
                onChange={(e) => handleIconUpload(e.target.files[0])}
                className="hidden"
              />
              <p className="text-[10px] text-center text-gray-400 mt-6 font-bold uppercase tracking-widest">
                SVG, PNG OR JPG • Max 2MB
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetIntegration;
